import { useState, useCallback, useMemo } from 'react';
import { parseCustomRange } from '../shared/utils/dateRangeUtils';

/**
 * Preset option for date range selection
 */
export interface DateRangePreset {
  label: string;
  value: string;
  getDates: () => { start: Date; end: Date };
}

/**
 * Date range state managed by the hook
 */
export interface DateRangeState {
  startDate: Date | null;
  endDate: Date | null;
  selectedPreset: string;
  currentMonth: Date;
}

/**
 * Return type for useDateRangePicker hook
 */
export interface UseDateRangePickerReturn {
  state: DateRangeState;
  handlePresetClick: (preset: DateRangePreset) => void;
  handleDayClick: (date: Date) => void;
  navigateMonth: (direction: number) => void;
  isSelected: (date: Date) => boolean;
  isInRange: (date: Date) => boolean;
  isRangeStart: (date: Date) => boolean;
  isRangeEnd: (date: Date) => boolean;
}

// Hoisted constants. `label` is an i18n key — consumers must translate.
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: 'dashboard.filters.dateRange.today',
    value: 'Today',
    getDates: () => {
      const today = new Date();
      return { start: today, end: today };
    },
  },
  {
    label: 'dashboard.filters.dateRange.yesterday',
    value: 'Yesterday',
    getDates: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: yesterday };
    },
  },
  {
    label: 'dashboard.filters.dateRange.last7days',
    value: 'Last 7 days',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      return { start, end };
    },
  },
  {
    label: 'dashboard.filters.dateRange.last30days',
    value: 'Last 30 days',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { start, end };
    },
  },
  {
    label: 'dashboard.filters.dateRange.thisMonth',
    value: 'This Month',
    getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date();
      return { start, end };
    },
  },
  {
    label: 'dashboard.filters.dateRange.customRange',
    value: 'Custom Range',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { start, end };
    },
  },
];

// Hoisted utility functions
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const isToday = (date: Date): boolean => {
  const today = new Date();
  return isSameDay(date, today);
};

export const getDaysInMonth = (date: Date): (Date | null)[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const days: (Date | null)[] = [];

  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }

  return days;
};

const DEFAULT_PRESET = 'This Month';

/**
 * Custom hook for managing date range picker state and logic
 * Reduces component complexity by extracting state management
 */
export function useDateRangePicker(initialPreset?: string): UseDateRangePickerReturn {
  // Lazy initialization of state
  const [state, setState] = useState<DateRangeState>(() => {
    const presetValue = initialPreset || DEFAULT_PRESET;

    // If the caller passed a custom-encoded range, seed the calendar with
    // those dates so re-opening the picker shows the selection.
    const custom = parseCustomRange(presetValue);
    if (custom?.startDate && custom?.endDate) {
      const start = new Date(`${custom.startDate}T00:00:00`);
      const end = new Date(`${custom.endDate}T00:00:00`);
      return {
        startDate: start,
        endDate: end,
        selectedPreset: 'Custom Range',
        currentMonth: end,
      };
    }

    const preset = DATE_RANGE_PRESETS.find((p) => p.value === presetValue);
    const { start, end } = preset?.getDates() ?? { start: null, end: null };

    return {
      startDate: start,
      endDate: end,
      selectedPreset: presetValue,
      currentMonth: end || new Date(),
    };
  });

  const handlePresetClick = useCallback((preset: DateRangePreset) => {
    const { start, end } = preset.getDates();
    setState({
      startDate: start,
      endDate: end,
      selectedPreset: preset.value,
      currentMonth: end,
    });
  }, []);

  const handleDayClick = useCallback((date: Date) => {
    setState((prev) => {
      // No start date - set it
      if (!prev.startDate) {
        return {
          ...prev,
          startDate: date,
          endDate: null,
          selectedPreset: 'Custom Range',
        };
      }

      // Both dates exist - start fresh
      if (prev.endDate) {
        return {
          ...prev,
          startDate: date,
          endDate: null,
          selectedPreset: 'Custom Range',
        };
      }

      // Only start date exists - determine order
      if (date < prev.startDate) {
        return {
          ...prev,
          startDate: date,
          endDate: prev.startDate,
          selectedPreset: 'Custom Range',
        };
      }

      return {
        ...prev,
        endDate: date,
        selectedPreset: 'Custom Range',
      };
    });
  }, []);

  const navigateMonth = useCallback((direction: number) => {
    setState((prev) => {
      const newMonth = new Date(prev.currentMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return { ...prev, currentMonth: newMonth };
    });
  }, []);

  // Memoized selection check functions
  const isSelected = useCallback(
    (date: Date): boolean => {
      if (!state.startDate && !state.endDate) {
        return false;
      }
      if (state.startDate && isSameDay(date, state.startDate)) {
        return true;
      }
      if (state.endDate && isSameDay(date, state.endDate)) {
        return true;
      }
      return false;
    },
    [state.startDate, state.endDate],
  );

  const isInRange = useCallback(
    (date: Date): boolean => {
      if (!state.startDate || !state.endDate) {
        return false;
      }
      return date > state.startDate && date < state.endDate;
    },
    [state.startDate, state.endDate],
  );

  const isRangeStart = useCallback(
    (date: Date): boolean => {
      return Boolean(state.startDate && isSameDay(date, state.startDate) && state.endDate !== null);
    },
    [state.startDate, state.endDate],
  );

  const isRangeEnd = useCallback(
    (date: Date): boolean => {
      return Boolean(state.endDate && isSameDay(date, state.endDate) && state.startDate !== null);
    },
    [state.startDate, state.endDate],
  );

  return useMemo(
    () => ({
      state,
      handlePresetClick,
      handleDayClick,
      navigateMonth,
      isSelected,
      isInRange,
      isRangeStart,
      isRangeEnd,
    }),
    [state, handlePresetClick, handleDayClick, navigateMonth, isSelected, isInRange, isRangeStart, isRangeEnd],
  );
}
